import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import { setUser } from '../store/user'
import { apiGet, apiPost } from '../hooks/useApi'
import { db } from '../hooks/useDexieDB'
import { useSessionStorage } from '../hooks/useStorage'
import { isEmail, isRegCode } from '../hooks/useValidation'
import Layout from '../components/Layout'
import Footer from '../components/Footer'
import SelecFilters from '../components/Steps/SelecFilters'
import SelectData from '../components/Steps/SelectData'
import Order from '../components/Steps/Order'
import OrderForm from '../components/Steps/OrderForm'

export default function GlobalSample (): JSX.Element {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const tabs = [
    intl.formatMessage({ id: 'globalSample.step.selecFilters' }),
    intl.formatMessage({ id: 'globalSample.step.selectData' }),
    intl.formatMessage({ id: 'globalSample.step.order' })
  ]

  const steps = [
    'filters',
    'selection',
    'order',
    'orderForm',
    'orderPayment'
  ]

  const user = useSelector((state: any) => state.user)
  const [statsLoading, setStatsLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [activePage, setActivePage] = useSessionStorage('activePage', '/')
  const [activeStep, setActiveStep] = useSessionStorage('activeStep', steps[0])
  const [filterCategories, setFilterCategories] = useSessionStorage<DataCategory[]>('globalSample.filterCategories', [])
  const [filterValues, setFilterValues] = useSessionStorage<FilterValue[]>('globalSample.filterValues', [])
  const [codesUploaded, setCodesUploaded] = useSessionStorage('globalSample.codesUploaded', false)
  const [codesAccepted, setCodesAccepted] = useSessionStorage('globalSample.codesAccepted', 0)
  const [selectedDataFields, setSelectedDataFields] = useSessionStorage<DataField[]>('globalSample.selectedDataFields', [])
  const [quartersIncluded, setQuartersIncluded] = useSessionStorage('globalSample.quartersIncluded', 1)
  const [stats, setStats] = useSessionStorage<Stats>('globalSample.stats', {
    filtersCount: null,
    dataFieldsCount: null
  })
  const [fileFormat, setFileFormat] = useSessionStorage('selectSample.fileFormat', 'xlsx')
  const [formData, setFormData] = useSessionStorage<OrderFormData>('selectSample.formData', {
    isPrivateCustomer: false,
    email: '',
    regCode: '',
    info: '',
    acceptTerms: false
  })

  const filterAggregatedValues = useMemo(() => filterValues.map(filter => {
    switch (filter.filterType) {
      case 'hierarchical_linked_checkbox_interval':
        return {
          filterName: filter.filterName,
          filterValues: filter.filterValues.slice(2),
          filterIntervalBegin: filter.filterValues[0] !== '' ? parseInt(filter.filterValues[0]) : null,
          filterIntervalEnd: filter.filterValues[1] !== '' ? parseInt(filter.filterValues[1]) : null
        }
      case 'interval':
        return {
          filterName: filter.filterName,
          filterIntervalBegin: filter.filterValues[0] !== '' ? parseInt(filter.filterValues[0]) : null,
          filterIntervalEnd: filter.filterValues[1] !== '' ? parseInt(filter.filterValues[1]) : null
        }
      case 'date_interval':
        return {
          filterName: filter.filterName,
          filterIntervalBegin: filter.filterValues[0] !== '' ? filter.filterValues[0].substring(0, 10) : null,
          filterIntervalEnd: filter.filterValues[1] !== '' ? filter.filterValues[1].substring(0, 10) : null
        }
      default:
        return {
          filterName: filter.filterName,
          filterValues: filter.filterValues
        }
    }
  }), [filterValues])

  const isNextDisabled = useMemo(() => {
    if (statsLoading) return true

    switch (activeStep) {
      case 'filters':
        return filterValues.length === 0
      case 'selection':
        return selectedDataFields.length === 0
      case 'order':
        return false
      case 'orderForm':
        if (user !== null) return false

        return !formData.acceptTerms || !isEmail(formData.email) || (!formData.isPrivateCustomer && !isRegCode(formData.regCode))
      default:
        return true
    }
  }, [statsLoading, stats, activeStep, filterValues, selectedDataFields, formData])

  const nextButtonText = useMemo(() => {
    if (activeStep === 'orderForm') return intl.formatMessage({ id: 'footer.next.inquiry' })

    return intl.formatMessage({ id: 'footer.next.next' })
  }, [intl.locale, activeStep])

  useEffect(() => {
    setActivePage('/global-sample')
  }, [])

  useEffect(() => {
    async function getUser (): Promise<void> {
      const { error } = await apiGet('authGetUser')

      if (error === undefined) return

      dispatch(setUser(null))
    }

    getUser().catch(console.error)
  }, [activePage, activeStep])

  useEffect(() => {
    const targetDiv = document.getElementById('main-wrapper')
    if (targetDiv) {
      targetDiv.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [activeStep])

  useEffect(() => {
    async function getStats (): Promise<void> {
      setStatsLoading(true)

      const regCodes = await db.codesAccepted.toCollection().primaryKeys()
      const { filtersCount, dataFieldsCount, error } = await apiPost('getQueryStatInternational', {
        body: {
          filters: filterAggregatedValues,
          excludedRegCodes: regCodes.length > 0 ? regCodes : null,
          selectedDataFields: selectedDataFields.length > 0
            ? selectedDataFields.map(x => ({
              dataFieldName: x.dataFieldName,
              quartersIncluded: x.quartersIncluded
            }))
            : null
        }
      })

      setStats({
        filtersCount: null,
        dataFieldsCount: null
      })

      setStatsLoading(false)

      if (error !== undefined) return

      setStats({
        filtersCount: filtersCount ?? null,
        dataFieldsCount: dataFieldsCount ?? null
      })
    }

    getStats().catch(console.error)
  }, [filterAggregatedValues, codesAccepted, selectedDataFields])

  function onPreviousStep (): any {
    const index = steps.findIndex(x => x === activeStep) - 1

    if (index < 0) navigate('/')

    setActiveStep(steps[index])
  }

  async function onNextStep (): Promise<void> {
    let index = steps.findIndex(x => x === activeStep) + 1

    if (index > steps.length - 1) index = steps.length - 1

    if (activeStep === 'orderForm') {
      await onPayment()
    } else {
      setActiveStep(steps[index])
    }
  }

  async function onPayment (): Promise<void> {
    if (paymentLoading) return

    setStatsLoading(true)
    setPaymentLoading(true)

    const regCodes = await db.codesAccepted.toCollection().primaryKeys()
    const { error } = await apiPost('postMAQueryInternational', {
      body: {
        filters: filterAggregatedValues,
        excludedRegCodes: regCodes.length > 0 ? regCodes : null,
        selectedDataFields: selectedDataFields.length > 0
          ? selectedDataFields.map(x => ({
            dataFieldName: x.dataFieldName,
            quartersIncluded: x.quartersIncluded
          }))
          : null,
        fileFormat,
        email: formData.email,
        regCode: !formData.isPrivateCustomer ? parseInt(formData.regCode ?? '') : null,
        info: formData.info
      }
    })

    setStatsLoading(false)
    setPaymentLoading(false)

    if (error !== undefined) return

    navigate('/inquiry-success')
  }

  const layoutFooter = (
    <Footer
      isNextDisabled={isNextDisabled}
      isStatsLoading={statsLoading}
      nextButtonText={nextButtonText}
      onNextStep={onNextStep}
      onPreviousStep={onPreviousStep}
      stats={stats}
    />
  )

  return (
    <Layout footer={layoutFooter}>
      {{
        filters: (
          <SelecFilters
            codesAccepted={codesAccepted}
            codesUploaded={codesUploaded}
            filterCategories={filterCategories}
            filterValues={filterValues}
            forInternational
            page="global-list"
            setCodesAccepted={function (count: number) { setCodesAccepted(count) }}
            setCodesUploaded={function (value: boolean) { setCodesUploaded(value) }}
            setFilterCategories={function (values: DataCategory[]) { setFilterCategories(values) }}
            setFilterValues={function (values: FilterValue[]) { setFilterValues(values) }}
            tabs={tabs}
          />
        ),
        selection: (
          <SelectData
            forInternational
            quartersIncluded={quartersIncluded}
            selectedDataFields={selectedDataFields}
            setQuartersIncluded={function (quarters: number) { setQuartersIncluded(quarters) }}
            setSelectedDataFields={function (fields: DataField[]) { setSelectedDataFields(fields) }}
            tabs={tabs}
          />
        ),
        order: (
          <Order
            fileFormat={fileFormat}
            filterCategories={filterCategories}
            filterValues={filterValues}
            selectedDataFields={selectedDataFields}
            setFileFormat={function (format: string) { setFileFormat(format) }}
            stats={stats}
            tabs={tabs}
          />
        ),
        orderForm: (
          <OrderForm
            formData={formData}
            setFormData={function (data: OrderFormData) { setFormData(data) }}
            stats={stats}
            tabs={tabs}
          />
        )
      }[activeStep]}
    </Layout>
  )
}
